// Send blocks follow the reply the CSR is actually sending (2026-10-27,
// docs/contact-view.md "What can stop Send";
// docs/decisions/2026-10-27-send-blocks-follow-reply.md).
//
// Two kinds of reason stop Send:
//   - reply-content blocks read the text in the reply box (a placeholder left,
//     the Signifyd mention, a Walmart link, the invoice claim, the promise
//     check, To / Subject missing) and already follow the CSR's edits;
//   - step blocks come from a step or a required choice (a task_path_choice
//     with no choice or no date, a step whose input is not filled in, a
//     return's "Check if scrap"). This file decides when a step block applies.
//
// A step block applies only while the reply depends on the step:
//   - the reply is still empty apart from the signature and the step's
//     templates are how it gets filled ('empty');
//   - the reply contains one of the step's dependsOnTokens ('token');
//   - the reply still is one of the step's dependsOnTemplates, by sentence
//     overlap ('template');
//   - the reply promises what the step does (a refund, replacement, return
//     label or cancellation, @cxhub/shared's findPromisedActions()) ('promise');
//   - the step declares nothing the reply could depend on ('undeclared'): the
//     block stays, and only "My reply doesn't need this" lifts it.
// Otherwise the block is lifted: the CSR picked another template or wrote
// their own reply. A lifted step is recorded with the decision as
// skippedSteps: [{ type, reason }].
//
// Shared by the UI (SuggestionPanel.jsx, processAll.js) and the server's
// decision route, so the two never disagree. Pure: no calls.

import { stepPromiseKinds, findPromisedActions } from './promised-actions.js'
import { isSignatureOnlyReply } from './ai.js'
import { needsTaskPathChoice } from './tasks.js'
import { RMA_REFERENCE_TOKEN } from './rmartn-cust-pays.js'

// THE threshold: a reply "still is" a template when at least this share of
// the template's sentences are in it (normalized: case, whitespace,
// punctuation, numbers and the INSERT* values filled in are ignored; the
// signature is never a template sentence). 0.6 per Jenn's spec: a CSR who
// keeps most of the template is still sending it, one who keeps only the
// greeting or a stock line is not.
export const TEMPLATE_SENTENCE_MATCH_SHARE = 0.6
// A template sentence counts only with at least this many fixed words
// ("Hi INSERTNAME," and "Thanks," are in every template).
const MIN_SENTENCE_WORDS = 3

export const STEP_NOT_NEEDED_NOTE = 'Not needed for this reply'
export const STEP_MARK_NOT_NEEDED_LABEL = "My reply doesn't need this"
export const SKIP_REPLY_DOES_NOT_USE = 'reply_does_not_use'
export const SKIP_CSR_MARKED_NOT_NEEDED = 'csr_marked_not_needed'
export const SKIP_REASONS = Object.freeze([SKIP_REPLY_DOES_NOT_USE, SKIP_CSR_MARKED_NOT_NEEDED])

const TOKEN_RE = /\$?INSERT[A-Z0-9]+/g
const strings = (v) => (Array.isArray(v) ? v.filter((s) => typeof s === 'string' && s.trim()) : null)

// What in the reply depends on a step. Declared in the step's params where
// the step is built (dependsOnTemplates, dependsOnTokens); a task_path_choice
// stored before 2026-10-27 has neither, so its path reply templates and, when
// an option needs a date, INSERTDATE stand in. promiseKinds: what the step
// carries out, for the promise rule.
export function stepReplyDependencies(step, actions = []) {
  const p = step?.params ?? {}
  let templates = strings(p.dependsOnTemplates)
  let tokens = strings(p.dependsOnTokens)
  if (step?.type === 'task_path_choice') {
    if (!templates) templates = (actions ?? []).filter((a) => a?.type === 'task_reply_template' && a.taskPath && typeof a.params?.text === 'string' && a.params.text.trim()).map((a) => a.params.text)
    if (!tokens) tokens = (p.options ?? []).some((o) => o?.dateRequired) ? ['INSERTDATE'] : []
  }
  // The return step makes the RMA whose number an RMARTN Cust Pays reply's
  // Reference ID holds (2026-10-27, rmartn-cust-pays.js): a reply that keeps
  // the RMA token depends on it.
  if (step?.type === 'process_return_refund') tokens = [...(tokens ?? []), RMA_REFERENCE_TOKEN]
  return { templates: templates ?? [], tokens: tokens ?? [], promiseKinds: stepPromiseKinds(step?.type) }
}

// The reply as plain text: tags out, the common entities decoded.
function plain(text) {
  return String(text ?? '')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|h\d)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
}

// Lower case, numbers as one shape (a filled-in amount or date matches any
// other), everything but letters and digits as one space.
function norm(text) {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/'/g, '')
    .replace(/\d+(?:[.,/:-]\d+)*/g, '0')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function sentencesOf(text) {
  return plain(text).split(/(?<=[.!?])\s+|\n+/).map((s) => s.trim()).filter(Boolean)
}

// The share of a template's sentences found in the reply (0..1). A sentence
// is found when its fixed text, around any INSERT* token (the value filled
// in), appears in the reply in order.
export function templateSentenceShare(template, reply) {
  const hay = ` ${norm(plain(reply))} `
  const all = sentencesOf(template).map((s) => {
    const pieces = s.split(TOKEN_RE).map(norm).filter(Boolean)
    return { pieces, words: pieces.join(' ').split(' ').filter(Boolean).length }
  }).filter((s) => s.words > 0)
  const counted = all.some((s) => s.words >= MIN_SENTENCE_WORDS) ? all.filter((s) => s.words >= MIN_SENTENCE_WORDS) : all
  if (!counted.length) return 0
  let found = 0
  for (const s of counted) {
    let at = 0
    let ok = true
    for (const piece of s.pieces) {
      const k = hay.indexOf(` ${piece} `, at)
      if (k < 0) { ok = false; break }
      at = k + piece.length + 1
    }
    if (ok) found += 1
  }
  return found / counted.length
}

export function replyIsTemplate(template, reply) {
  return templateSentenceShare(template, reply) >= TEMPLATE_SENTENCE_MATCH_SHARE
}

function hasToken(text, token) {
  const t = String(token ?? '').replace(/^\$/, '')
  if (!t) return false
  return new RegExp(`(?<![A-Z0-9])\\$?${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![A-Z0-9])`).test(text)
}

// Why the reply depends on the step ('empty' | 'token' | 'template' |
// 'promise' | 'undeclared'), or null when it does not (the block is lifted).
// signature: the reply box's resolved signature, so a box holding only it
// counts as empty.
export function replyDependsOnStep(step, reply, { actions = [], signature = null } = {}) {
  const deps = stepReplyDependencies(step, actions)
  if (!deps.templates.length && !deps.tokens.length && !deps.promiseKinds.length) return 'undeclared'
  const text = plain(reply)
  if (deps.templates.length && isSignatureOnlyReply(text, signature ? plain(signature) : signature)) return 'empty'
  if (deps.tokens.some((t) => hasToken(text, t))) return 'token'
  if (deps.templates.some((t) => replyIsTemplate(t, text))) return 'template'
  if (deps.promiseKinds.length) {
    const promised = new Set(findPromisedActions(text).map((p) => p.kind))
    if (deps.promiseKinds.some((k) => promised.has(k))) return 'promise'
  }
  return null
}

// Whether a step's block on Send applies to this reply. notNeeded: the step
// indexes the CSR marked "My reply doesn't need this" (a Set or an array).
// { applies, reason }: reason is the skip reason when it does not apply.
export function stepBlockState(step, index, reply, { actions = [], signature = null, notNeeded = [] } = {}) {
  const marked = notNeeded instanceof Set ? notNeeded.has(index) : (notNeeded ?? []).map(Number).includes(index)
  if (marked) return { applies: false, reason: SKIP_CSR_MARKED_NOT_NEEDED }
  const why = replyDependsOnStep(step, reply, { actions, signature })
  return why ? { applies: true, reason: null, why } : { applies: false, reason: SKIP_REPLY_DOES_NOT_USE }
}

// The skippedSteps a decision body carries, cleaned: [{ index, reason }]
// for a real step index and a known reason, one per index.
export function cleanSkippedSteps(list, actions = []) {
  const out = []
  for (const s of Array.isArray(list) ? list : []) {
    const index = Number(s?.index)
    if (!Number.isInteger(index) || index < 0 || index >= (actions ?? []).length) continue
    if (!SKIP_REASONS.includes(s?.reason)) continue
    if (out.some((o) => o.index === index)) continue
    out.push({ index, reason: s.reason })
  }
  return out.sort((a, b) => a.index - b.index)
}

// The indexes the CSR marked "My reply doesn't need this", from a decision
// body's skippedSteps.
export function markedNotNeededIndexes(list, actions = []) {
  return cleanSkippedSteps(list, actions).filter((s) => s.reason === SKIP_CSR_MARKED_NOT_NEEDED).map((s) => s.index)
}

const stepLabel = (a, i) => `step ${i + 1}${a?.label ? ` (${a.label})` : ''}`

// The decision's record of the steps whose block was lifted: their types and
// why, once, when the suggestion is decided with any. Beside the unchecked
// steps' entry (step-checks.js's uncheckedDecisionEntry()).
export function skippedDecisionEntry({ skipped = [], actions = [], status = null, actedBy = null, at = new Date().toISOString() } = {}) {
  const list = cleanSkippedSteps(skipped, actions)
  if (!list.length) return null
  return {
    service: 'CXHub', method: 'DECISION', phase: 'approve', at, actedBy,
    label: `Decided (${status ?? 'unknown'}) with steps the reply did not need: ${list.map((s) => `${stepLabel(actions?.[s.index], s.index)}, ${s.reason === SKIP_CSR_MARKED_NOT_NEEDED ? 'marked not needed' : 'not used by the reply'}`).join('; ')}`,
    decisionSkipped: true,
    skippedSteps: list.map((s) => ({ type: actions?.[s.index]?.type ?? null, reason: s.reason })).filter((s) => s.type),
  }
}

// The decision's skippedSteps ([{ type, reason }]), [] when none.
export function skippedStepsFrom(apiCalls) {
  const list = Array.isArray(apiCalls) ? apiCalls : []
  for (let k = list.length - 1; k >= 0; k--) {
    const c = list[k]
    if (c?.decisionSkipped === true) return (Array.isArray(c.skippedSteps) ? c.skippedSteps : []).filter((s) => typeof s?.type === 'string' && SKIP_REASONS.includes(s?.reason))
  }
  return []
}

// The decision route's 409 task_path_not_chosen (2026-10-28): the plain
// sentence the panel shows on the choice step, and its index. A Return Status
// stage asks for the stage and the carrier's date; any other choice (a task's
// moved / not moved, a resolution) for a path. index: the step's index.
export const TASK_PATH_NOT_CHOSEN_ERROR = 'task_path_not_chosen'
export function taskChoiceBlockMessage(step, index) {
  const n = Number(index) + 1
  return step?.params?.kind === 'return_stage'
    ? `Choose where the return is and enter the carrier's date (step ${n}) before sending.`
    : `Choose a path on step ${n} before sending.`
}

// The task choice's Send block (a task_path_choice with no choice, or a
// Return Status stage with no date), following the reply: null when there is
// no block, else { index, applies, reason }. The UI's Send and the server's
// decision route both ask this.
export function taskChoiceSendBlock(suggestion, reply, { signature = null, notNeeded = [] } = {}) {
  if (!needsTaskPathChoice(suggestion)) return null
  const actions = suggestion?.proposedActions ?? []
  const index = actions.findIndex((a) => a?.type === 'task_path_choice')
  if (index < 0) return null
  return { index, ...stepBlockState(actions[index], index, reply, { actions, signature, notNeeded }) }
}
